/**
 * Theme Keywords Map
 * Used for fuzzy classification of content into high-level themes
 * 
 * RULES:
 * - Technology requires STRONG tech signals (ai, coding, software, etc.)
 * - Generic device words (phone, tablet, screen) are NOISE and don't count
 * - Clothing/fashion signals OVERRIDE weak tech signals
 */

export const THEME_KEYWORDS = {
  Technology: [
    // STRONG tech signals only - no generic device words
    "ai", "artificial intelligence", "automation", "automations",
    "machine learning", "ml", "llm", "neural network", "neural networks",
    "software", "saas", "productivity tool", "chrome extension",
    "code", "coding", "programming", "developer", "engineering",
    "data science", "data engineering", "apis", "api",
    "infrastructure", "cloud", "cloud computing", "devtools",
    "startup tool", "analytics", "algorithms", "algorithm",
    "model", "models", "backend", "frontend", "robotics",
    "cybersecurity", "blockchain", "deep learning"
  ],
  Business: [
    "business", "growth", "market", "strategy", "pricing", "sales", "revenue",
    "company", "organization", "startup", "scale", "leadership", "negotiation",
    "valuation", "founder", "ceo", "investor", "venture capital", "fundraising",
    "b2b", "b2c", "enterprise", "pitch", "deal"
  ],
  Creativity: [
    "design", "creativity", "art", "visual", "aesthetics", "branding", "identity",
    "story", "taste", "creative direction", "illustration", "graphics",
    "typography", "color theory", "composition", "artistic"
  ],
  HealthFitness: [
    "health", "fitness", "nutrition", "wellbeing", "exercise", "training",
    "diet", "strength", "workout", "gym", "cardio", "yoga", "meditation",
    "wellness", "bodybuilding", "athlete", "sports"
  ],
  Psychology: [
    "psychology", "mindset", "mental", "behavior", "emotions", "motivation",
    "identity", "self awareness", "cognitive", "therapy", "anxiety",
    "emotional intelligence", "habits", "personality"
  ],
  Productivity: [
    "productivity", "focus", "workflow", "output", "optimization",
    "time management", "efficiency", "deep work", "planning", "goals",
    "task management", "organization"
  ],
  WealthCareer: [
    "career", "wealth", "finance", "investing", "job", "skills",
    "professional", "promotion", "salary", "income", "resume",
    "networking", "interview", "freelance", "side hustle"
  ],
  Lifestyle: [
    "lifestyle", "routine", "travel", "minimalism", "daily life",
    "vacation", "adventure", "experience", "living", "morning routine",
    "evening routine", "self care"
  ]
};

/**
 * NOISE KEYWORDS - These are IGNORED during classification
 * They do not count toward any theme score
 * Generic device/photo/scene words that don't indicate real theme intent
 */
export const NOISE_KEYWORDS = [
  // Generic device words (NOT tech signals)
  "phone", "phones", "mobile", "smartphone", "smartphones",
  "device", "devices", "tablet", "tablets", "screen", "screens",
  "display", "displays", "computer", "computers", "laptop", "laptops",
  // Generic descriptors
  "modern", "digital", "digital lifestyle", "content", "creator", "influencer",
  "technology", // The word "technology" alone is too generic without context
  // Photo/image words
  "photo", "photos", "image", "images", "picture", "pictures",
  "portrait", "selfie", "photography",
  // People descriptors
  "man", "woman", "male", "female", "person", "people", "human",
  // Scene/setting words
  "stairs", "balcony", "outdoors", "outside", "indoor", "indoors",
  "room", "living room", "sofa", "chair", "building", "street",
  "wall", "floor", "window", "door", "background",
  // Pose/action words
  "pose", "posed", "posing", "standing", "sitting", "walking", "looking",
  "casual", "relaxed",
  // Filler words
  "nice", "good", "great", "beautiful", "amazing", "cool", "awesome"
];

/**
 * CLOTHING KEYWORDS - Signals fashion/style content
 * If present, these OVERRIDE weak tech signals
 */
export const CLOTHING_KEYWORDS = [
  // Fashion terms
  "fashion", "style", "outfit", "outfits", "clothing", "wardrobe", "closet",
  "menswear", "womenswear", "streetwear", "runway", "lookbook",
  // Specific clothing items
  "dress", "dresses", "jacket", "coat", "blazer", "suit", "suits",
  "shirt", "t-shirt", "tee", "hoodie", "sweater", "jeans", "pants",
  "skirt", "shorts", "trousers",
  // Footwear
  "heels", "boots", "sneakers", "shoes", "loafers",
  // Accessories
  "watch", "bag", "handbag", "sunglasses", "jewelry", "hat", "cap",
  // Fashion industry
  "model", "models", "editorial", "look", "fit", "aesthetic",
  "designer", "collection", "trend", "chic", "couture",
  // Brands (common fashion)
  "gucci", "louis vuitton", "prada", "nike", "adidas", "zara"
];

/**
 * LIFESTYLE KEYWORDS - Signals lifestyle/personal content
 * If present, these OVERRIDE weak tech signals
 */
export const LIFESTYLE_KEYWORDS = [
  "lifestyle", "home", "interior", "decor", "decoration",
  "travel", "vacation", "trip", "destination", "sunset", "sunrise",
  "coffee", "cafe", "restaurant", "food", "cooking", "recipe",
  "gym", "fitness", "wellness", "routine", "day in the life",
  "morning", "evening", "weekend", "relaxing", "chill",
  "nature", "beach", "mountain", "park", "garden",
  "family", "friends", "social", "party", "celebration",
  "hobby", "hobbies", "leisure", "entertainment"
];

/**
 * CEO-energy micro-summaries for each theme
 */
export const THEME_MICRO_SUMMARIES = {
  Technology: "You explore ideas that give you leverage, speed, and smarter systems.",
  Business: "You track anything tied to growth, strategy, or better decision-making.",
  Creativity: "You pull in concepts that expand your thinking and sharpen your taste.",
  HealthFitness: "You study ideas that build energy, health, and long-term performance.",
  Psychology: "You explore mental patterns and frameworks that shape human behavior.",
  Productivity: "You refine workflows and systems to move faster with less effort.",
  WealthCareer: "You focus on decisions that grow your income, skills, and influence.",
  Lifestyle: "You collect ideas that shape how you live, move, and choose daily priorities."
};
